import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import { loadGetPlanState, saveGetPlanState, clearGetPlanState } from '../utils/indexedDB';
import { plansApi, Plan, CreatePlanPayload, UpdatePlanPayload } from '../utils/api/plans';
import './GetPlan.css';

// SVG Icons
const FolderIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>);
const PlusIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const CloseIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>);
const FilterIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>);

const SearchIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>);

const GetPlan: React.FC = () => {
  const { getAuthHeaders } = useAuth();
  
  // Plans List State
  const [plans, setPlans] = useState<Plan[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 15;
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Drawer Toggle State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Sidebar Filter & Search State
  const [filterState, setFilterState] = useState<string>('');
  const [filterDispatch, setFilterDispatch] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Active Plan Editor State
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [planName, setPlanName] = useState<string>('');
  const [emailFile, setEmailFile] = useState<File | null>(null);
  const [emailText, setEmailText] = useState<string>('');
  const [dispatchMode, setDispatchMode] = useState<string>('both');
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [isStateLoaded, setIsStateLoaded] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch plans whenever page, filters, or search query change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPlans(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, filterDispatch, searchQuery]);

  // Load browser cache on mount (only for unsaved writing)
  useEffect(() => {
    loadGetPlanState().then((state) => {
      if (state && !activePlan) {
        if (state.planName) setPlanName(state.planName);
        if (state.emailText) setEmailText(state.emailText);
        if (state.emailFile) setEmailFile(state.emailFile);
        if (state.dispatchMode) setDispatchMode(state.dispatchMode);
      }
      setIsStateLoaded(true);
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save to browser cache while user is writing (only if not saved yet)
  useEffect(() => {
    if (isStateLoaded && !activePlan) {
      if (emailText.trim() || planName.trim()) {
        saveGetPlanState({ planName, emailText, emailFile, dispatchMode }).catch(console.error);
      }
    }
  }, [planName, emailText, emailFile, dispatchMode, isStateLoaded, activePlan]);

  const fetchPlans = async (p: number) => {
    setLoadingPlans(true);
    try {
      const res = await plansApi.getPlans(
        getAuthHeaders,
        p,
        limit,
        filterState || undefined,
        filterDispatch || undefined,
        searchQuery || undefined
      );
      setPlans(res.plans);
      setTotalPlans(res.total);
      setPage(p);
    } catch (err) {
      console.error("Failed to fetch plans", err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSelectPlan = (plan: Plan) => {
    setActivePlan(plan);
    setPlanName(plan.name || '');
    setEmailText(plan.email_text || '');
    setDispatchMode(plan.dispatch_mode || 'both');
    setEmailFile(null);
    setError('');
    setStatus('');
    setIsDrawerOpen(false); // Close drawer after selection for smooth experience
  };

  const handleNewPlan = () => {
    setActivePlan(null);
    setPlanName('');
    setEmailText('');
    setDispatchMode('both');
    setEmailFile(null);
    setError('');
    setStatus('');
    setIsDrawerOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setEmailFile(f);
    const reader = new FileReader();
    reader.onload = () => setEmailText(String(reader.result || ''));
    reader.readAsText(f);
  };

  const buildFormData = () => {
    const fd = new FormData();
    if (emailFile) {
      fd.append('email', emailFile);
    } else if (emailText.trim()) {
      fd.append('email_text', emailText);
    }
    fd.append('dispatch_mode', dispatchMode);
    return fd;
  };

  const autoGeneratePlanName = (text: string) => {
    if (planName.trim()) return planName.trim();

    const claimMatch = text.match(/(?:claim|case|policy|ref)\s*#?\s*:?\s*([a-z0-9-]+)/i);
    const d = new Date();
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    if (claimMatch && claimMatch[1]) {
      return `Inspection Plan - Claim #${claimMatch[1].toUpperCase()}`;
    }
    
    const modeLabel = dispatchMode === 'both' ? 'Engineer & Drone' : dispatchMode === 'engineer' ? 'Engineer Only' : 'Drone Only';
    return `Plan (${modeLabel}) - ${dateStr}`;
  };

  const handleSaveDraft = async () => {
    const currentEmailText = emailText.trim();
    if (!currentEmailText) {
      setError('Please provide email text before saving.');
      return;
    }

    setLoading(true);
    setError('');
    setStatus(activePlan ? 'Updating plan...' : 'Saving draft plan...');

    const nameToSave = autoGeneratePlanName(currentEmailText);

    try {
      if (activePlan) {
        // Update existing plan (preserve state)
        const payload: UpdatePlanPayload = {
          name: nameToSave,
          state: activePlan.state,
          email_text: currentEmailText,
          dispatch_mode: dispatchMode,
        };
        const updated = await plansApi.updatePlan(getAuthHeaders, activePlan.id, payload);
        setActivePlan(updated);
        setPlans(plans.map(p => p.id === updated.id ? updated : p));
        await clearGetPlanState();
        setStatus('Plan updated successfully.');
      } else {
        // Create new draft
        const payload: CreatePlanPayload = {
          name: nameToSave,
          state: 'draft',
          dispatch_mode: dispatchMode,
          email_text: currentEmailText,
        };
        const created = await plansApi.createPlan(getAuthHeaders, payload);
        setActivePlan(created);
        setPlans([created, ...plans]);
        await clearGetPlanState();
        setStatus('Saved as draft.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save draft');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const currentEmailText = emailText.trim();
    if (!currentEmailText) {
      setError('Please upload or paste an assignment email.');
      return;
    }

    setLoading(true);
    setError('');
    
    const isRegenerating = activePlan?.state === 'generated';
    let planIdToUpdate = activePlan?.id;
    const nameToSave = autoGeneratePlanName(currentEmailText);

    try {
      if (!planIdToUpdate) {
        setStatus('Creating plan...');
        const payload: CreatePlanPayload = {
          name: nameToSave,
          state: 'draft',
          dispatch_mode: dispatchMode,
          email_text: currentEmailText,
        };
        const created = await plansApi.createPlan(getAuthHeaders, payload);
        planIdToUpdate = created.id;
        setActivePlan(created);
        setPlans([created, ...plans]);
      }

      setStatus(isRegenerating ? 'Regenerating inspection plan with AI…' : 'Generating inspection plan with AI…');

      const fd = buildFormData();
      fd.append('bypass_cache', 'true');
      const res = await axios.post(`${API_URL}/api/inspection-plan/generate`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() },
        withCredentials: true,
        timeout: 120000,
      });
      const generatedPlanJson = res.data?.plan ?? null;

      if (generatedPlanJson && planIdToUpdate) {
        const updated = await plansApi.updatePlan(getAuthHeaders, planIdToUpdate, {
          name: nameToSave,
          state: 'generated',
          generated_json: generatedPlanJson,
          email_text: currentEmailText,
          dispatch_mode: dispatchMode
        });
        setActivePlan(updated);
        setPlans(plans.map(p => p.id === updated.id ? updated : p));
        await clearGetPlanState();
        setStatus('Inspection plan generated successfully!');
      } else {
        throw new Error("Failed to generate plan JSON from response");
      }

    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to generate plan');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!activePlan || activePlan.state !== 'generated' || !activePlan.generated_json) return;
    
    setLoading(true);
    setError('');
    setStatus('Building PDF document…');
    try {
      const response = await fetch(`${API_URL}/api/inspection-plan/pdf`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(activePlan.generated_json),
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      
      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        return;
      }
      if (!response.ok) {
        const j = await response.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'PDF build failed');
      }
      const blob = await response.blob();
      const filename = activePlan.generated_json?.pdf_filename || 'inspection_plan.pdf';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: any) {
      setError(err?.message || 'PDF download failed');
    } finally {
      setLoading(false);
      setStatus('');
    }
  };

  const renderPlanSummary = () => {
    if (!activePlan || !activePlan.generated_json) return null;
    const planObj = activePlan.generated_json;
    const meta = planObj.case_metadata || {};
    const ep = planObj.execution_plan || {};
    return (
      <div className="plan-result">
        <div className="plan-result-header">
          <h2>Inspection Plan Details</h2>
          <span className="plan-path-pill">{meta.target_inspection_path || '—'}</span>
        </div>
        <div className="plan-meta-grid">
          {[
            ['Claim', meta.claim_number],
            ['Insured', meta.insured_name],
            ['Carrier', meta.insurance_carrier],
            ['Location', meta.property_address],
            ['Date of Loss', meta.reported_date_of_loss],
            ['Inspector', meta.assigned_inspector],
            ['Created At', activePlan.created_at ? new Date(activePlan.created_at).toLocaleString() : '—'],
            ['Updated At', activePlan.updated_at ? new Date(activePlan.updated_at).toLocaleString() : '—'],
          ].map(([k, v]) => (
            <div key={k} className="plan-meta-item">
              <span className="plan-meta-label">{k}</span>
              <span className="plan-meta-value">{v || '—'}</span>
            </div>
          ))}
        </div>

        <details open className="plan-section">
          <summary>Dispatch Summary</summary>
          <p>{planObj.dispatch_summary}</p>
        </details>

        {(planObj.hidden_forensic_clues || []).length > 0 && (
          <details className="plan-section">
            <summary>Hidden Forensic Clues</summary>
            <ul>{planObj.hidden_forensic_clues.map((c: string, i: number) => <li key={i}>{c}</li>)}</ul>
          </details>
        )}

        {(planObj.pre_field_triggers || []).length > 0 && (
          <details className="plan-section">
            <summary>Pre-Field Triggers</summary>
            <ul>
              {planObj.pre_field_triggers.map((t: any, i: number) => (
                <li key={i}><b>{t.trigger}</b> — <i>{t.condition}</i><br />{t.action}</li>
              ))}
            </ul>
          </details>
        )}

        {Object.keys(planObj.field_protocol || {}).map((key) => {
          const steps = planObj.field_protocol[key] || [];
          if (!steps.length) return null;
          return (
            <details key={key} className="plan-section">
              <summary>{key.replace(/_/g, ' ')}</summary>
              <ol>
                {steps.map((s: any, i: number) => (
                  <li key={i}>
                    <div className="plan-step-action">{s.action}</div>
                    {s.purpose && <div className="plan-step-purpose">Purpose: {s.purpose}</div>}
                  </li>
                ))}
              </ol>
            </details>
          );
        })}

        {(ep.sequence || []).length > 0 && (
          <details className="plan-section">
            <summary>Execution Sequence</summary>
            <ol>{ep.sequence.map((s: string, i: number) => <li key={i}>{s}</li>)}</ol>
          </details>
        )}

        {(ep.equipment_list || []).length > 0 && (
          <details className="plan-section">
            <summary>Equipment Checklist</summary>
            <ul>{ep.equipment_list.map((e: string, i: number) => <li key={i}>☐ {e}</li>)}</ul>
          </details>
        )}

        {(ep.decision_branches || []).length > 0 && (
          <details className="plan-section">
            <summary>Decision Branches</summary>
            <ul>
              {ep.decision_branches.map((b: any, i: number) => {
                if (typeof b === 'string') return <li key={i}>{b}</li>;
                const cond = String(b.condition || '').replace(/^if\s+/i, '');
                return <li key={i}><b>IF</b> {cond} <b>→ THEN</b> {b.follow_up}</li>;
              })}
            </ul>
          </details>
        )}

        <div className="plan-json-toggle">
          <details>
            <summary>Raw JSON</summary>
            <pre>{JSON.stringify(planObj, null, 2)}</pre>
          </details>
        </div>
      </div>
    );
  };

  return (
    <div className="get-plan-page">
      <Navigation />

      {/* Drawer Overlay Backdrop */}
      {isDrawerOpen && (
        <div className="drawer-backdrop" onClick={() => setIsDrawerOpen(false)} />
      )}

      {/* Slide-over Saved Plans Drawer */}
      <div className={`get-plan-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="drawer-title">
            <FolderIcon />
            <h2>
              Saved Plans ({totalPlans})
              {(filterState || filterDispatch || searchQuery) && <span className="filtered-header-badge">Filtered</span>}
            </h2>
          </div>
          <div className="drawer-header-actions">
            <button className="drawer-new-btn" onClick={handleNewPlan} title="Create New Plan">
              <PlusIcon /> New
            </button>
            <button className="drawer-close-btn" onClick={() => setIsDrawerOpen(false)} title="Close Drawer">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="sidebar-search-box">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search plans by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sidebar-search-input"
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={() => setSearchQuery('')}>×</button>
          )}
        </div>

        {/* Filters Bar */}
        <div className="sidebar-filters">
          <div className="filter-label"><FilterIcon /> Filter:</div>
          <select
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
            className="sidebar-filter-select"
          >
            <option value="">All States</option>
            <option value="draft">Drafts</option>
            <option value="generated">Generated</option>
          </select>

          <select
            value={filterDispatch}
            onChange={(e) => setFilterDispatch(e.target.value)}
            className="sidebar-filter-select"
          >
            <option value="">All Modes</option>
            <option value="both">Eng + Drone</option>
            <option value="engineer">Engineer</option>
            <option value="drone">Drone</option>
          </select>
        </div>

        {/* Active Filter Chips / Indicator Bar */}
        {(filterState || filterDispatch || searchQuery) && (
          <div className="active-filters-bar">
            <span className="active-filter-title">Active:</span>
            {searchQuery && (
              <span className="filter-chip" onClick={() => setSearchQuery('')}>
                Search: "{searchQuery}" ×
              </span>
            )}
            {filterState && (
              <span className="filter-chip" onClick={() => setFilterState('')}>
                State: {filterState} ×
              </span>
            )}
            {filterDispatch && (
              <span className="filter-chip" onClick={() => setFilterDispatch('')}>
                Mode: {filterDispatch} ×
              </span>
            )}
            <button
              className="clear-all-filters-btn"
              onClick={() => { setFilterState(''); setFilterDispatch(''); setSearchQuery(''); }}
            >
              Reset
            </button>
          </div>
        )}
        
        <div className="plan-list">
          {loadingPlans ? (
            <p className="sidebar-msg">Loading plans...</p>
          ) : plans.length === 0 ? (
            <p className="sidebar-msg">No plans found.</p>
          ) : (
            plans.map(p => (
              <div 
                key={p.id} 
                className={`plan-item ${activePlan?.id === p.id ? 'active' : ''}`}
                onClick={() => handleSelectPlan(p)}
              >
                <div className="plan-item-header">
                  <span className="plan-name">{p.name}</span>
                  <span className={`plan-state ${p.state}`}>{p.state}</span>
                </div>
                <div className="plan-item-meta">
                  <span>Created: {new Date(p.created_at).toLocaleDateString()}</span>
                  <span> • </span>
                  <span>Updated: {new Date(p.updated_at).toLocaleDateString()}</span>
                  <span> • </span>
                  <span>{p.dispatch_mode === 'both' ? 'Eng & Drone' : p.dispatch_mode}</span>
                </div>
              </div>
            ))
          )}
        </div>
        
        {totalPlans > limit && (
          <div className="pagination">
            <button disabled={page === 1} onClick={() => fetchPlans(page - 1)}>Prev</button>
            <span>Page {page} of {Math.ceil(totalPlans / limit)}</span>
            <button disabled={page * limit >= totalPlans} onClick={() => fetchPlans(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Main Full-width Layout */}
      <div className="get-plan-layout">
        <div className="get-plan-main">
          {/* Top Control Bar with Drawer Trigger & Active Plan Breadcrumb */}
          <div className="top-control-bar">
            <div className="top-control-left">
              <button className="open-drawer-btn" onClick={() => setIsDrawerOpen(true)}>
                <FolderIcon /> Saved Plans ({totalPlans})
                {(filterState || filterDispatch || searchQuery) && <span className="filtered-dot">•</span>}
              </button>

              {activePlan && (
                <div className="top-breadcrumb">
                  <span className="breadcrumb-separator">/</span>
                  <span className="breadcrumb-plan-name">{activePlan.name}</span>
                  <span className={`plan-state ${activePlan.state}`}>{activePlan.state}</span>
                  <div className="top-breadcrumb-dates">
                    <span className="date-chip">
                      <span className="date-label">Created:</span> {new Date(activePlan.created_at).toLocaleDateString()}
                    </span>
                    <span className="date-chip">
                      <span className="date-label">Updated:</span> {new Date(activePlan.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <button className="start-new-top-btn" onClick={handleNewPlan}>
              <PlusIcon /> New Plan
            </button>
          </div>

          <div className="get-plan-header">
            <h1>{activePlan ? activePlan.name : 'New Inspection Plan'}</h1>
            <p className="get-plan-subtitle">
              Upload a carrier assignment email or paste its text to generate a forensic inspection plan.
            </p>
          </div>

          <div className="get-plan-card">
            {/* Plan Name Field */}
            <div className="plan-name-input-group">
              <label htmlFor="plan-name-input">Plan Name</label>
              <input
                id="plan-name-input"
                type="text"
                className="plan-name-input"
                placeholder="e.g. Inspection Plan - Claim #12345 (Auto-generated if left empty)"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
              />
            </div>

            <div className="get-plan-input">
              <label className="get-plan-upload-label">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.eml,text/plain"
                  onChange={handleFileChange}
                />
                <span>{emailFile ? emailFile.name : 'Choose assignment email (.txt)'}</span>
              </label>
              <textarea
                placeholder="…or paste the assignment email text here"
                value={emailText}
                onChange={(e) => { setEmailText(e.target.value); setEmailFile(null); }}
                rows={10}
              />
            </div>

            <div className="get-plan-dispatch-select">
              <label htmlFor="dispatch-mode">Dispatch Mode:</label>
              <select
                id="dispatch-mode"
                value={dispatchMode}
                onChange={(e) => setDispatchMode(e.target.value)}
                disabled={loading}
              >
                <option value="both">Engineer & Drone Operator</option>
                <option value="engineer">Engineer Only</option>
                <option value="drone">Drone Operator Only</option>
              </select>
            </div>
            
            <div className="get-plan-actions">
              <button className="get-plan-btn secondary" onClick={handleSaveDraft} disabled={loading}>
                {loading && (status.includes('Saving') || status.includes('Updating'))
                  ? (activePlan ? 'Updating...' : 'Saving...')
                  : (activePlan ? 'Update Plan' : 'Save Draft')}
              </button>
              
              <button className="get-plan-btn primary" onClick={handleGenerate} disabled={loading}>
                {loading && status.includes('Generating')
                  ? (activePlan?.state === 'generated' ? 'Regenerating Plan…' : 'Generating Plan…')
                  : (activePlan?.state === 'generated' ? 'Regenerate Plan' : 'Generate Plan')}
              </button>
              
              {activePlan && activePlan.state === 'generated' && (
                <button className="get-plan-btn secondary download-pdf-btn" onClick={handleDownloadPdf} disabled={loading}>
                  Download Plan PDF
                </button>
              )}
            </div>

            {status && <p className="get-plan-status">{status}</p>}
            {error && <p className="get-plan-error">{error}</p>}
            
            {activePlan && activePlan.state === 'generated' && activePlan.generated_json && (
              <div className="get-plan-quick-actions">
                <a
                  href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(activePlan.generated_json, null, 2))}`}
                  download={activePlan.generated_json.pdf_filename?.replace(/\.pdf$/, '.json') || 'inspection_plan.json'}
                  className="get-plan-link"
                >
                  Download JSON
                </a>
              </div>
            )}
          </div>

          {activePlan && activePlan.state === 'generated' && renderPlanSummary()}
        </div>
      </div>
    </div>
  );
};

export default GetPlan;